// =============================================================================
// MeuDriver — Admin Dashboard / KPI row
// =============================================================================

import { BookOpen, Clock, TrendingUp, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'

export interface KpiData {
  totalApproved: number
  totalPending: number
  lessonsThisMonth: number
  revenueThisMonth: number
  totalStudents: number
}

interface KpiCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  loading: boolean
  badge?: { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
  subtitle?: string
}

function KpiCard({ title, value, icon, loading, badge, subtitle }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">{value}</span>
            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </div>
        )}
        {subtitle && !loading && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  )
}

export function AdminKPICards({ kpi, loading }: { kpi: KpiData; loading: boolean }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <KpiCard
        title="Total de Instrutores"
        value={kpi.totalApproved}
        icon={<Users className="h-5 w-5" />}
        loading={loading}
        subtitle="Instrutores aprovados"
      />
      <KpiCard
        title="Alunos Cadastrados"
        value={kpi.totalStudents}
        icon={<Users className="h-5 w-5" />}
        loading={loading}
        subtitle="Estudantes na plataforma"
      />
      <KpiCard
        title="Aguardando Aprovação"
        value={kpi.totalPending}
        icon={<Clock className="h-5 w-5" />}
        loading={loading}
        badge={kpi.totalPending > 0 ? { label: `${kpi.totalPending} pendente(s)`, variant: 'destructive' } : undefined}
        subtitle="Cadastros em análise"
      />
      <KpiCard
        title="Aulas Este Mês"
        value={kpi.lessonsThisMonth}
        icon={<BookOpen className="h-5 w-5" />}
        loading={loading}
        subtitle="Aulas concluídas"
      />
      <KpiCard
        title="Receita da Plataforma"
        value={formatCurrency(kpi.revenueThisMonth)}
        icon={<TrendingUp className="h-5 w-5" />}
        loading={loading}
        subtitle="Taxas recebidas este mês"
      />
    </div>
  )
}
